import { Button } from '@suscri/ui';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthShell, authInputClass, authLabelClass } from '../components/AuthShell';
import { trpc } from '../lib/trpc';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const reset = trpc.auth.resetPassword.useMutation({
    onSuccess: () => navigate('/login?reset=ok'),
    onError: event => setError(event.message),
  });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('Las contraseñas no coinciden.'));
      return;
    }
    reset.mutate({ token, newPassword: password });
  };

  return (
    <AuthShell compact title={t('Nueva contraseña')} subtitle={t('Elige una contraseña segura para tu cuenta.') }>
      {!token ? (
        <div className="rounded-xl border border-danger/25 bg-danger/10 p-4 text-sm leading-6 text-danger" role="alert">
          {t('Falta el token del enlace. Solicita de nuevo la recuperación desde')}{' '}
          <Link to="/forgot-password" className="font-semibold underline underline-offset-2">{t('aquí')}</Link>.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <PasswordField id="new-password" label={t('Nueva contraseña')} value={password} onChange={setPassword} placeholder={t('Mínimo 8 caracteres')} minLength={8} />
          <PasswordField id="confirm-password" label={t('Confirmar contraseña')} value={confirm} onChange={setConfirm} placeholder={t('Repite la contraseña')} />
          {error && <p className="rounded-xl border border-danger/25 bg-danger/10 p-3.5 text-sm font-medium text-danger" role="alert">{error}</p>}
          <Button type="submit" className="w-full" size="lg" disabled={reset.isPending}>
            {reset.isPending ? t('Guardando…') : t('Cambiar contraseña')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

function PasswordField({ id, label, value, onChange, placeholder, minLength }: { id: string; label: string; value: string; onChange: (value: string) => void; placeholder: string; minLength?: number }) {
  return (
    <div>
      <label htmlFor={id} className={authLabelClass}>{label}</label>
      <div className="relative">
        <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input id={id} type="password" required minLength={minLength} autoComplete="new-password" placeholder={placeholder} value={value} onChange={event => onChange(event.target.value)} className={`${authInputClass} pl-10`} />
      </div>
    </div>
  );
}
